import React from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Chip,
  Fade
} from '@mui/material';
import { getStatusIcon, getStatusText } from './DownloadPageHelpers';

export const DownloadProgressSection = ({ currentProgress, settledCount, totalFiles, completedCount, errorCount, activeTheme, isDark }) => (
  <Box 
    className="progress-section"
    sx={{
      padding: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      marginBottom: 2
    }}
  >
    <Box className="progress-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
      <Typography 
        variant="subtitle1" 
        className="progress-label"
        sx={{ fontWeight: 500 }}
      >
        Download Progress
      </Typography>
      <Typography 
        variant="body2" 
        className="progress-percentage"
        sx={{ 
          fontWeight: 500, 
          fontSize: '1rem',
          color: activeTheme?.palette?.primary?.main || '#1976D2'
        }}
      >
        {Math.round(currentProgress)}%
      </Typography>
    </Box>
    <Box className="progress-bar-container" sx={{ marginBottom: 1 }}>
      <LinearProgress
        variant="determinate"
        value={currentProgress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: activeTheme?.palette?.primary?.main || '#1976D2',
          }
        }}
      />
    </Box>
    <Typography 
      variant="caption" 
      color="text.secondary" 
      className="progress-stats"
    >
      {settledCount} of {totalFiles} files processed
      {completedCount > 0 && (
        <span> • {completedCount} completed</span>
      )}
      {errorCount > 0 && (
        <span> • {errorCount} failed</span>
      )}
    </Typography>
  </Box>
);

export const FileDownloadList = ({ files, fileStatuses, downloadStatus }) => (
  <Box className="file-list-section" sx={{ marginBottom: 2 }}>
    <Typography 
      variant="subtitle2" 
      className="file-list-title"
      sx={{ 
        fontWeight: 500, 
        marginBottom: 1,
        fontSize: '0.875rem'
      }}
    >
      Files
    </Typography>
    <Paper 
      className="file-list-container" 
      elevation={0}
      sx={{
        maxHeight: downloadStatus === 'completed' || downloadStatus === 'partial_failure' ? 300 : 400,
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        padding: 0.5
      }}
    >
      <List className="file-list" sx={{ padding: 0 }}>
        {files.map((file, index) => {
          const status = fileStatuses[index] || 'pending';
          const fileName = file.split('/').pop() || file;
          const rawPath = file.includes('/') ? file.substring(0, file.lastIndexOf('/')) : '';
          const filePath = rawPath === 'pending-upload' ? '' : rawPath;
          
          return (
            <Fade in={true} timeout={300} key={index}>
              <ListItem 
                className={`file-list-item file-list-item-${status}`}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  padding: 1.5,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
                  {getStatusIcon(status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography 
                      variant="body2" 
                      className="file-name" 
                      noWrap
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem'
                      }}
                    >
                      {fileName}
                    </Typography>
                  }
                  secondary={
                    filePath && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        className="file-path" 
                        noWrap
                        sx={{ fontSize: '0.75rem', marginTop: 0.25 }}
                      >
                        {filePath}
                      </Typography>
                    )
                  }
                  sx={{ marginLeft: 1, flex: 1, minWidth: 0 }}
                />
                <Chip
                  label={getStatusText(status)}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    marginLeft: 1, 
                    fontWeight: 500,
                    borderColor: 'divider',
                  }}
                />
              </ListItem>
            </Fade>
          );
        })}
      </List>
    </Paper>
  </Box>
);